/**
 * LogoLabPage
 * Temporary preview page to compare 3 candidate AskIsaac logos side by side
 * against the current one, all rendered inside a realistic navy → brand-blue
 * header bar identical to the production site header.
 *
 * Route: /logo-lab  (not linked from anywhere — private URL for stakeholder review)
 */
import { Link } from "react-router-dom";

const STERILEMATE_LOGO =
  "/logos/sterilemate-white.png?v=4";
const CURRENT_ASKISAAC =
  "https://customer-assets.emergentagent.com/job_frontline-hero-1/artifacts/8nkngflm_AskIsaac%20Logo%20perm.png";

const CANDIDATES = [
  {
    id: "current",
    title: "0. Current (baseline)",
    subtitle: "The logo in production right now — bracket [i] + two-tone wordmark.",
    src: CURRENT_ASKISAAC,
    note: "Dated chatbot aesthetic, two competing typefaces, [i] disappears at small sizes.",
  },
  {
    id: "a",
    title: "A. Pure Wordmark (McKesson-mode)",
    subtitle: "One geometric sans, no icon. 'Ask' white + 'Isaac' cyan.",
    src: "/logos/askisaac-a-wordmark.svg",
    note: "Ships in an hour. Ages 20 years. Single-typeface discipline.",
  },
  {
    id: "b",
    title: "B. Wordmark + Circle Monogram",
    subtitle: "Cyan circle badge with lowercase 'i' + wordmark. Doubles as favicon / app icon.",
    src: "/logos/askisaac-b-monogram.svg",
    note: "My pick — scalable brand mark, works standalone, still clean.",
  },
  {
    id: "c",
    title: "C. Speech-Bubble 'i' + Wordmark",
    subtitle: "Rounded speech bubble containing 'i' — signals 'ask' without brackets.",
    src: "/logos/askisaac-c-speech.svg",
    note: "Most branded of the three. Slightly more conversational tone.",
  },
];

function HeaderMockup({ src, alt }) {
  return (
    <div
      className="rounded-lg overflow-hidden border border-slate-200 shadow-sm"
      style={{
        background:
          "linear-gradient(90deg, #050a16 0%, #0b1e3f 32%, #123a75 65%, #0056A8 100%)",
      }}
    >
      <div className="relative h-20 flex items-center px-6">
        {/* left dark fade — matches real header */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none w-[440px]"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,10,22,0.85) 0%, rgba(5,10,22,0.55) 45%, rgba(5,10,22,0) 100%)",
          }}
        />
        <div className="relative flex items-center gap-3">
          <img
            src={STERILEMATE_LOGO}
            alt="Sterile Mate"
            className="h-12 w-auto"
           
          />
          <span className="text-white/70 font-medium italic tracking-wide text-xs">
            powered by
          </span>
          <img src={src} alt={alt} className="h-8 w-auto" />
        </div>
      </div>
    </div>
  );
}

export default function LogoLabPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="logo-lab-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Private · Stakeholder Review
            </p>
            <h1 className="text-2xl font-black text-slate-900 mt-1">
              AskIsaac Logo Lab
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Three candidate marks vs. the current logo — all rendered inside
              the real production header for accurate comparison.
            </p>
          </div>
          <Link
            to="/"
            className="text-sm font-semibold text-blue-700 hover:text-blue-900"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {CANDIDATES.map((c) => (
          <section
            key={c.id}
            data-testid={`logo-candidate-${c.id}`}
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
          >
            <div className="px-6 pt-5 pb-3">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900">{c.title}</h2>
                <span className="text-xs font-mono text-slate-500">{c.src}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{c.subtitle}</p>
            </div>
            <div className="px-6 pb-6">
              <HeaderMockup src={c.src} alt={`AskIsaac candidate ${c.id}`} />
              <p className="text-xs text-slate-500 mt-3 italic">{c.note}</p>
            </div>
          </section>
        ))}

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-900">
          <p className="font-bold mb-1">How to pick</p>
          <p>
            Reply with <span className="font-mono bg-white px-1.5 py-0.5 rounded">A</span>,{" "}
            <span className="font-mono bg-white px-1.5 py-0.5 rounded">B</span>, or{" "}
            <span className="font-mono bg-white px-1.5 py-0.5 rounded">C</span> and I&apos;ll
            swap it in as the site-wide AskIsaac logo (all pages, all breakpoints).
            The SVGs are editable — colors, kerning, and the icon shape can all be
            tuned before you trademark.
          </p>
        </div>
      </main>
    </div>
  );
}
